import React, { useState, useEffect, useRef } from "react";
import { Howl } from 'howler';
import './App.css';

function App() {
  const [transcriptions, setTranscriptions] = useState([]);
  const [selectedTranscription, setSelectedTranscription] = useState(null);
  const [selectedWord, setSelectedWord] = useState({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [sprite, setSprite] = useState(null);
  const [sound, setSound] = useState(null);
  const [wordSet, setWordSet] = useState([]);
  const [wordToIdxMap, setWordToIdxMap] = useState({});
  const [guidToWordMap, setGuidToWordMap] = useState({});
  const [seek, setSeek] = useState(0);
  const [duration, setDuration] = useState(0);
  const requestRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch('/api/transcriptions', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        });
        const data = await response.json();
        setTranscriptions(data);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, []);

  const handleLinkClick = async (itemId) => {
    try {
      setSelectedTranscription(itemId);
      const response = await fetch(`/api/transcriptions/${itemId}/words`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      const data = await response.json();
      const wordSpriteMap = {};
      setWordSet(data.wordSet);
      data.wordSet.forEach((item, idx) => {
        wordSpriteMap[item.guid] = [item.startPos, (item.endPos - item.startPos)];
        wordToIdxMap[item.guid] = idx;
        guidToWordMap[item.guid] = item;
        guidToWordMap[item.guid].highlighted = false;
      });
      const spriteSound = new Howl({
        src: [`/api/transcriptions/${itemId}/audio`],
        format: ['mp3'],
        sprite: wordSpriteMap,
        preload: true,
      });
      const fullSound = new Howl({
        src: [`/api/transcriptions/${itemId}/audio`],
        format: ['mp3'],
        preload: true,
      });
      setSprite(spriteSound);
      setSound(fullSound);
    } catch (error) {
      console.error('Error fetching words:', error);
    }
  };

  const handlePlay = () => {
    if (isPlaying) {
      setIsPlaying(!isPlaying);
      sound.pause();
      cancelAnimationFrame(requestRef.current)
    } else {
      setIsPlaying(!isPlaying);
      sound.play();
      setDuration(sound.duration() * 1000);
      renderSeek();
    }

  };

  const renderSeek = () => {
      setSeek(sound.seek()*1000);
      requestRef.current = requestAnimationFrame(renderSeek)
  };

  const adjustSeek = (e) => {
      setSeek(e.target.value);
      sound.seek(e.target.value/1000);
  };

  const handleWordClick = (guid) => {
    sprite.play(guid);
    selectedWord.highlighted = false;
    guidToWordMap[guid].highlighted = true;
    setSelectedWord(guidToWordMap[guid]);
    setSeek(p => {return guidToWordMap[guid].startPos});
    sound.seek(guidToWordMap[guid].startPos/1000);
  };

  const handleWordChange = (e, field) => {
    const wordIdx = wordToIdxMap[selectedWord.guid];
    setSelectedWord({ ...selectedWord, [field]: e.target.value });
    wordSet[wordIdx][field] = e.target.value;
  };

  const handleSave = () => {
    const wordIdx = wordToIdxMap[selectedWord.englishText];
    const itemId = selectedTranscription;
    wordSet[wordIdx] = selectedWord;
    fetch(`/api/transcriptions/${itemId}/words`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ wordSet }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        setWordSet(data.wordSet);
        // You can add additional logic here, such as showing a success message
      })
      .catch(error => {
        console.error('Error saving words:', error);
        // You can add error handling logic here, such as showing an error message
      });
  };


  return (
    <div className="App">
      <div className="container text-center">
        <div className="row">
          <div className="col-6">
            <div className="word-container">
              {guidToWordMap && Object.keys(guidToWordMap)
              .sort((a, b) => guidToWordMap[a].startPos - guidToWordMap[b].startPos)
              .map((guid) => (
                <React.Fragment><div className={guidToWordMap[guid].highlighted && 'highlighted'} onClick={() => handleWordClick(guid)} key={guid}>{guidToWordMap[guid].englishText}</div></React.Fragment>
              ))}
            </div>
          </div>
          <div className="col-6">
            <ul className="App-links-list">
              {transcriptions.map((item) => (
                <li key={item.guid}>
                  <a href="#" onClick={() => handleLinkClick(item.guid)}>{item.guid}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="row">
              <div className="col-1">
                <button className="btn btn-primary me-2" onClick={() => handlePlay()} disabled={sound === null}>
                  {isPlaying ? <i className="bi bi-pause-fill"></i> : <i className="bi bi-play-fill"></i>}
                </button>
              </div>
              <div className="col-11 play-slider">
                <input
                  type="range"
                  title={seek}
                  className="form-range flex-grow-1"
                  min="0"
                  max={duration}
                  value={seek}
                  onChange={adjustSeek}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="row">
          <div className="col">
            <div className="test-container">
              <div className="input-group mb-3">
                <span className="input-group-text">English Text</span>
                <input type="text" className="form-control" value={selectedWord.englishText ?? ''} onChange={e => handleWordChange(e, 'englishText')} />
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text">Akan Text</span>
                <input type="text" className="form-control" value={selectedWord.akanText ?? ''} onChange={e => handleWordChange(e, 'akanText')} />
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text">Start Position</span>
                <input type="text" className="form-control" value={selectedWord.startPos ?? ''} onChange={e => handleWordChange(e, 'startPos')} />
              </div>
              <div className="input-group mb-3">
                <span className="input-group-text">End Position</span>
                <input type="text" className="form-control" value={selectedWord.endPos ?? ''} onChange={e => handleWordChange(e, 'endPos')} />
              </div>
              <button className="btn btn-primary me-2" onClick={() => handleSave()}>Save</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
